import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { i18n } from '../../../translate/i18n';
import { Paper, Container, Table, TableBody, TableCell, TableFooter, TablePagination, TableRow, Typography, Avatar, Box, IconButton, CircularProgress, Chip, Tooltip, Stack, Button, Drawer } from '@mui/material';
import { FilterList, Delete as DeleteIcon } from '@mui/icons-material';
import * as actions from "../../../store/actions";
import Spinner from '../../../components/Spinner/index';
import EnhancedTableHead from "../../../UI/MaterialTableOverrides/EnhancedTableHead";
import TableTitle from '../../../UI/MaterialTableOverrides/TableTitle';
import TablePaginationActionsWrapped from "../../../UI/MaterialTableOverrides/TablePaginationActions";
import { RootState } from "../../../store/types";
import moment from 'moment';
import { renderStatusColor, renderStatusIcon } from "../../../utils/statusManipulation";
import ServiceDraftsFilterModal from "../ModalFilter/ServiceDrafts";
import confirm from "../../../UI/Confirm/confirm";
import { RouteComponentProps } from 'react-router-dom';

const TableServiceDrafts: React.FC<RouteComponentProps> = ({ history, location }) => {
  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(5);
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<string>('desc');

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  const loading = useSelector((state: RootState) => state.serviceDrafts.loadingServiceDrafts);
  const serviceDrafts = useSelector((state: RootState) => state.serviceDrafts.serviceDrafts);
  const serviceDraftsCount = useSelector((state: RootState) => state.serviceDrafts.serviceDraftsCount);
  const loadingDelete = useSelector((state: RootState) => state.serviceDrafts.loadingDelete);
  const filteredSites = useSelector((state: any) => state.sites.selectedSitesPlaces);
  const filteredPlaces = useSelector((state: RootState) => state.serviceDrafts.filteredPlaces);
  const filteredEquipment = useSelector((state: RootState) => state.serviceDrafts.filteredEquipment);
  const filteredServiceTypes = useSelector((state: RootState) => state.serviceDrafts.filteredServiceTypes);
  const filteredFrequencies = useSelector((state: RootState) => state.serviceDrafts.filteredFrequencies);
  const filteredModels = useSelector((state: RootState) => state.serviceDrafts.filteredModels);
  const filterCreatedAtStart = useSelector((state: RootState) => state.serviceDrafts.filterCreatedAtStart);
  const filterCreatedAtEnd = useSelector((state: RootState) => state.serviceDrafts.filterCreatedAtEnd);
  const filterModifiedAtStart = useSelector((state: RootState) => state.serviceDrafts.filterModifiedAtStart);
  const filterModifiedAtEnd = useSelector((state: RootState) => state.serviceDrafts.filterModifiedAtEnd);
  const deletingDraftId = useSelector((state: RootState) => state.serviceDrafts.deletingDraftId);

  const fetchServicesDraft = async (newPage: number) => dispatch(actions.serviceDraftsFetch({ page: newPage, limit, orderBy, order, filteredEquipment, filteredServiceTypes, filteredFrequencies, filteredPlaces, filteredModels, filteredSites, filterCreatedAtStart, filterCreatedAtEnd, filterModifiedAtStart, filterModifiedAtEnd}));
  const deleteServiceDraft = async (id: string) => dispatch(actions.serviceDraftsDeleteDraft(id, { page, limit, orderBy, order, filteredEquipment, filteredServiceTypes, filteredFrequencies, filteredPlaces, filteredModels, filteredSites, filterCreatedAtStart, filterCreatedAtEnd, filterModifiedAtStart, filterModifiedAtEnd}));

  useEffect(() => {
    fetchServicesDraft(1);
    setPage(1);
  }, [limit, orderBy, order, filteredEquipment, filteredServiceTypes, filteredFrequencies, filteredPlaces, filteredModels, filteredSites, filterCreatedAtStart, filterCreatedAtEnd, filterModifiedAtStart, filterModifiedAtEnd, dispatch]);

  const headRows = [
    { id: "equipment", numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.equipment') },
    { id: "service", numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.service') },
    { id: "createdAt", numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.createdAt') },
    { id: "lastModifiedAt", numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.lastModifiedAt') },
    { id: "place", numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.place') },
    { id: "actions", numeric: false, disablePadding: false, label: i18n.t('tableServiceDrafts.actions') },
  ];

  const handleChangePage = (element: any, page: number) => {
    setPage(page + 1);
    fetchServicesDraft(page + 1);
  };

  const handleChangeRowsPerPage = (element: any) => {
    setLimit(element?.target?.value);
    setPage(1);
  }; 

  const styles = {
    table: {
      position: 'relative',
      marginBottom: 50,
    },
    tableRow: {
    },
    opacityContainer: {
      opacity: loading ? '0.3' : '1',
    },
    spinner: {
      position: 'absolute',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      top: '-5px',
      opacity: '1',
      zIndex: 2,
    },
    noResults: {
      height: '100px',
    },
    deleteTextConfirm: {
      marginLeft: '-30px',
      fontWeight: 'bold',
    },
    editWidth: {
      width: '90%'
    },
    thumbUp: {
      marginRight: 15,
      color: "#349c34"
    },
    thumbDown: {
      marginRight: 15,
      color: "#d04545"
    },
    avatar: {
      width: '2.75rem',
      height: '2.75rem',
      margin: '2px 0.25rem 2px 0.25rem',
    },
    avatarContainer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    companyName: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: '0.75rem',
    },
    avatarSmallText: {
      fontSize: '0.75rem',
      width: '2.75rem',
      height: '2.75rem',
      margin: '2px 0.25rem 2px 0.25rem',
    },
    chip: {
      margin: 5,
    },
    statusContainer: {
      display: 'flex',
      alignItems: 'center',
    },
    cell: {
      fontSize: '0.775rem',
      padding: '0.25rem 2',
    },
    equipmentName: {
      display: 'flex' as 'flex',
      flexDirection: 'column' as 'column',
      marginLeft: '0.75rem',
    },
    status: {
      marginRight: '0.25rem'
    },
    cellPhotoContainer: {
      display: 'flex' as 'flex',
      alignItems: 'center' as 'center',
      flexDirection: 'row' as 'row',
      marginLeft: '0.5rem',
    },
    filterContainer: {
      position: 'relative',
      width: '100%',
    },
    filterChipContainer: {
      paddingLeft: 20,
      display: 'flex' as 'flex',
      flexWrap: 'wrap' as 'wrap',
    },
    filterChip: {
      cursor: 'pointer' as 'pointer',
      margin: 5,
    },
    buttonFilter: {
      zIndex: 200,
      backgroundColor: 'green',
      height: '2.2rem',
      width: '6.5rem',
      padding: '1rem',
      color: '#fff',
    },
    buttonFilterIcon: {
      marginLeft: 5
    },
    clearFilter: {
      cursor: 'pointer',
      textDecoration: 'underline',
      zIndex: 200,
      padding: '0.75rem 0.5rem',
      color: '#000',
    },
  }

  const renderChips = (filters: {label: string, value: string} [], removeFilter: (filter: {label: string, value: string}) => void) => (filters.map((filter) => (
    <Chip
      key={filter.value}
      label={filter.label}
      onDelete={() => removeFilter(filter)}
      style={styles.filterChip}
    />
  )))

  const removeFilteredSite = (site: any) => {
    dispatch(actions.deleteFilteredSite(site))
  }

  const renderSiteChips = (sites: {_id: string, name: string} []) => (sites.map((site) => (
    <Chip
      key={site._id}
      label={site.name}
      onDelete={() => removeFilteredSite(site)}
      style={styles.filterChip}
    />
  )))

  const renderCreatedAtStartDateChip = () => (<Chip
    label={`${i18n.t('tableServiceDrafts.fromCreatedAt')}: ${moment(filterCreatedAtStart).format('DD/MM/YYYY')}`}
    onDelete={() => dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart: null,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }))}
    style={styles.filterChip}
  />)
  const renderCreatedAtEndDateChip = () => (<Chip
    label={`${i18n.t('tableServiceDrafts.toCreatedAt')} ${moment(filterCreatedAtEnd).format('DD/MM/YYYY')}`}
    onDelete={() => dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd: null,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }))}
    style={styles.filterChip}
  />)

  const renderModifiedAtStartDateChip = () => (<Chip
    label={`${i18n.t('tableServiceDrafts.fromModifiedAt')}: ${moment(filterModifiedAtStart).format('DD/MM/YYYY')}`}
    onDelete={() => dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart: null,
      filterModifiedAtEnd,
    }))}
    style={styles.filterChip}
  />)
  const renderModifiedAtEndDateChip = () => (<Chip
    label={`${i18n.t('tableServiceDrafts.toModifiedAt')}: ${moment(filterModifiedAtEnd).format('DD/MM/YYYY')}`}
    onDelete={() => dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd: null,
    }))}
    style={styles.filterChip}
  />)


  const removePlaceFilter = (filter: { label: string; value: string }) => {
    const newPlaceFilters = filteredPlaces.filter((f) => f.value !== filter.value);

    dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces: newPlaceFilters,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }));
  };

  const removeEquipmentFilter = (_filter: { label: string; value: string }) => {
    dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment: null,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }));
  };

  const removeServiceTypeFilter = (filter: { label: string; value: string }) => {
    const newServiceTypeFilters = filteredServiceTypes.filter((f) => f.value !== filter.value);

    dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes: newServiceTypeFilters,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }));
  };

  const removeFrequencyFilter = (filter: { label: string; value: string }) => {
    const newFrequencyFilters = filteredFrequencies.filter((f) => f.value !== filter.value);

    dispatch(actions.serviceDraftsSetFilters({
      filteredModels,
      filteredFrequencies: newFrequencyFilters,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }));
  };

  const removeModelFilter = (filter: {label: string, value: string}) => {
    const newModelFilters = filteredModels.filter((f) => f.value !== filter.value);
    dispatch(actions.serviceDraftsSetFilters({
      filteredModels: newModelFilters,
      filteredFrequencies,
      filteredPlaces,
      filteredServiceTypes,
      filteredEquipment,
      filterCreatedAtStart,
      filterCreatedAtEnd,
      filterModifiedAtStart,
      filterModifiedAtEnd,
    }));
  }
  
  const handleClickClearFilters = () => {
    dispatch(actions.serviceDraftsClearFilters());
  }

  const clearFilterButton = () => {
    if ((filteredServiceTypes && filteredServiceTypes.length > 0)
      || (filteredPlaces && filteredPlaces.length > 0)
      || (filteredModels && filteredModels.length > 0)
      || (filteredFrequencies && filteredFrequencies.length > 0)
      || (filteredEquipment)
    ) return <Typography onClick={handleClickClearFilters} style={styles.clearFilter}>{i18n.t('btnClearFilterManagementService.Typography') as string}</Typography>;
  };

  const handleDeleteServiceDraft = async (draftId: string) => {
    confirm(i18n.t('tableServiceDrafts.deleteDraftMessage'),
      {
        title: i18n.t('tableServiceDrafts.deleteDraftTitle'),
        titleColor: 'red',
        disableBackdrop: true
      }
    ).then(
      () => {},
      async () => await deleteServiceDraft(draftId),
    )
  }

  const handleGoToDraft = async (draftId: string, equipmentId: string, equipmentService: any) => {
    dispatch(actions.createServiceSelectEquipment(equipmentId))
    dispatch(actions.createServiceFetchEquipmentServices(equipmentId));
    dispatch(actions.createServiceSelectEquipmentService(equipmentService));
    dispatch(actions.createServiceSetServiceDraftId(draftId));
    history.push("/form-new-service/2", { from: location.pathname });
  }

  return (
    <Container maxWidth="xl">
      <Drawer
        variant="temporary"
        anchor="right"
        onClose={() => setIsFilterOpen(false)}
        open={isFilterOpen}>
        <ServiceDraftsFilterModal
          closeModal={() => setIsFilterOpen(false)}
        />
      </Drawer>
      <Paper>
        <TableTitle title={i18n.t('tableServiceDrafts.pageTitle')} hideSearchInput />
        <Box style={styles.filterChipContainer}>
          <Button
            variant='contained'
            onClick={() => setIsFilterOpen(true)}
            style={styles.buttonFilter}>
          {i18n.t('btnManagementService.label')as string} <FilterList style={styles.buttonFilterIcon} />
          </Button>
          {renderSiteChips(filteredSites)}
          {renderChips(filteredModels, removeModelFilter)}
          {renderChips(filteredFrequencies, removeFrequencyFilter)}
          {renderChips(filteredPlaces, removePlaceFilter)}
          {renderChips(filteredServiceTypes, removeServiceTypeFilter)}
          {filteredEquipment && <Chip label={filteredEquipment.label} onDelete={() => removeEquipmentFilter(filteredEquipment)} style={styles.filterChip} />}
          {filterCreatedAtStart && renderCreatedAtStartDateChip()}
          {filterCreatedAtEnd && renderCreatedAtEndDateChip()}
          {filterModifiedAtStart && renderModifiedAtStartDateChip()}
          {filterModifiedAtEnd && renderModifiedAtEndDateChip()}
          {clearFilterButton()}
        </Box>
        <Table sx={styles.table}>
          <tbody>
            <tr>
              <td>
                {loading &&
                  <Box sx={styles.spinner}>
                    <Spinner overlay />
                  </Box>}
                {serviceDrafts.length === 0 &&
                  <div style={styles.noResults}>
                    {!loading && <Typography sx={styles.spinner}>{i18n.t('tableTitleCompany.Typography') as string}</Typography>}
                  </div>}
              </td>
            </tr>
          </tbody>
          <EnhancedTableHead
            headRows={headRows}
            order={order}
            orderBy={orderBy}
            onRequestSort={() => {}}
          />
          <TableBody style={styles.opacityContainer}>
            {serviceDrafts.map((draft) => (
              <TableRow
                style={styles.tableRow}
                key={draft._id}
              >
                <TableCell style={styles.cell}>
                  <Box style={styles.cellPhotoContainer}>
                    <Box style={styles.status}>{renderStatusIcon(draft.equipment)}</Box>
                      {draft.equipment.photo ?
                        <Avatar
                          src={draft.equipment.photo.url}
                          style={{
                            ...styles.avatar,
                            border: `2px solid ${renderStatusColor(draft.equipment)}`
                          }}
                        />
                        :
                        <Avatar
                            style={{
                              ...styles.avatarSmallText,
                              border: `2px solid ${renderStatusColor(draft.equipment)}`
                            }}
                        >
                          FOTO
                        </Avatar>
                      }
                    <Box style={styles.equipmentName}>
                      <Box>{draft.equipmentModel.name}</Box>
                      <Box style={{ color: 'grey' }}>{draft.equipment.serialNumber}</Box>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell style={styles.cell}>{`${draft.serviceType.name} ${draft.frequency.name}`}</TableCell>
                <TableCell style={styles.cell}>{moment(draft.createdAt).format('DD/MM/YYYY HH:mm')}</TableCell>
                <TableCell style={styles.cell}>{moment(draft.lastModifiedAt).format('DD/MM/YYYY HH:mm')}</TableCell>
                <TableCell style={styles.cell}>{draft.place.path}</TableCell>
                <TableCell style={styles.cell}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      onClick={async () => {await handleGoToDraft(draft._id, draft.equipment._id, draft.equipmentService)}}
                      disabled={loadingDelete}
                    >
                      {i18n.t('tableServiceDrafts.finishService') as string}
                    </Button>
                    <IconButton
                      size="small"
                      color="inherit"
                      onClick={async () => await handleDeleteServiceDraft(draft._id)}
                      disabled={loadingDelete}
                    >
                      { (loadingDelete && deletingDraftId === draft._id) ? <CircularProgress size={14} /> : <DeleteIcon fontSize="small" /> }
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter style={styles.opacityContainer}>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                count={serviceDraftsCount}
                rowsPerPage={limit}
                page={page - 1}
                SelectProps={{
                  native: true
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActionsWrapped}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </Paper>
    </Container>
  )
}

export default TableServiceDrafts;

